import React, { Fragment, memo, useMemo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FormikRichTextEditor } from 'src/components/shared/form/formik/FormikRichTextEditor'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikSwitch } from 'src/components/shared/form/formik/FormikSwitch'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { InfoContainer } from 'src/components/shared/InfoContainer'
import { INSURANCE_APPROVAL_TYPE_OPTIONS } from 'src/constants/insurance-policy/constants'
import { PAYMENT_PERIOD_OPTIONS } from 'src/constants/payment/constants'
import type { DocusignTemplate } from 'src/interfaces/docusignTemplate'
import type { InsuranceProduct } from 'src/interfaces/insuranceProduct'
import type {
  InsurancePolicyProductFormMode,
  InsurancePolicyProductFormValues,
} from './InsurancePolicyProductForm.interface'

const NO_CONTRACT_OPTION: BasicSelectOption = { label: 'None', value: '' }

interface Props {
  mode: InsurancePolicyProductFormMode
  selectableProducts: InsuranceProduct[]
  docusignTemplates: DocusignTemplate[]
  /** The already-assigned product's display name, shown read-only in edit mode. */
  lockedProductName: string
  /** The already-set frequency, shown read-only in edit mode. */
  lockedFeeFrequency: string
}

const InsurancePolicyProductFormContentComponent = ({
  mode,
  selectableProducts,
  docusignTemplates,
  lockedProductName,
  lockedFeeFrequency,
}: Props) => {
  const { values } = useFormikContext<InsurancePolicyProductFormValues>()

  const isEdit = mode === 'edit'

  const productOptions = useMemo<BasicSelectOption[]>(() => {
    return selectableProducts.map((insuranceProduct) => {
      return { label: insuranceProduct.name, value: insuranceProduct.id }
    })
  }, [selectableProducts])

  const contractOptions = useMemo<BasicSelectOption[]>(() => {
    const templateOptions = docusignTemplates.map((docusignTemplate) => {
      return { label: docusignTemplate.title ?? docusignTemplate.id, value: docusignTemplate.id }
    })

    return [NO_CONTRACT_OPTION, ...templateOptions]
  }, [docusignTemplates])

  return (
    <div css={tw`flex flex-col gap-6`}>
      <div css={tw`flex flex-col gap-4`}>
        <Typography variant="h4" css={tw`text-shades-900`}>
          Basics
        </Typography>
        {isEdit ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="h6" css={tw`text-shades-400`}>
              Insurance product
            </Typography>
            <Typography variant="body2">{lockedProductName}</Typography>
            <Typography variant="body2" css={tw`text-shades-500`}>
              Can&apos;t be changed after creation — it keeps existing contractor records
              consistent.
            </Typography>
          </div>
        ) : (
          <FormikSelect<InsurancePolicyProductFormValues>
            name="insuranceProductId"
            label="Insurance product"
            options={productOptions}
            placeholder="Select a product…"
          />
        )}
        <FormikSwitch<InsurancePolicyProductFormValues>
          name="allowOptOut"
          label="Allow contractors to opt out"
          description="When off, coverage is required."
        />
        {values.allowOptOut && (
          <Fragment>
            <FormikSwitch<InsurancePolicyProductFormValues>
              name="requireExpirationDate"
              label="Require Expiration Date when contractor uploads own insurance (opt-out)"
            />
            <InfoContainer dataTestId="insurance-policy-product-require-expiration-date-info">
              Applies only to insurance uploaded by the contractor. If the contractor opts in to
              GigSafe coverage, the expiration date is not required.
            </InfoContainer>
          </Fragment>
        )}
      </div>

      <div css={tw`flex flex-col gap-4`}>
        <Typography variant="h4" css={tw`text-shades-900`}>
          Pricing
        </Typography>
        {/* Entered in CENTS, matching the Payments tab in this app — the other money form
            these admins use — and avoiding a dollars-to-cents rounding step on a value the
            backend validates as @IsInt. */}
        <FormikTextField<InsurancePolicyProductFormValues>
          name="feeCents"
          label="Flat fee (cents)"
          placeholder="e.g. 500 for $5.00"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="feePercent"
          label="Percent fee (%)"
          placeholder="e.g. 2.5"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="feeCapCents"
          label="Maximum total fee (cents)"
          placeholder="e.g. 5000 for $50.00"
        />
        {isEdit ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="h6" css={tw`text-shades-400`}>
              Charge frequency
            </Typography>
            <Typography variant="body2">{lockedFeeFrequency}</Typography>
            <Typography variant="body2" css={tw`text-shades-500`}>
              Locked after creation so past charges stay accurate.
            </Typography>
          </div>
        ) : (
          <FormikSelect<InsurancePolicyProductFormValues>
            name="feeFrequency"
            label="Charge frequency"
            options={PAYMENT_PERIOD_OPTIONS}
            placeholder="Select a frequency…"
          />
        )}
      </div>

      <div css={tw`flex flex-col gap-4`}>
        <Typography variant="h4" css={tw`text-shades-900`}>
          Contract &amp; documents
        </Typography>
        <FormikSelect<InsurancePolicyProductFormValues>
          name="insuranceDocusignTemplateId"
          label="Contract (DocuSign template)"
          options={contractOptions}
          placeholder="No contract"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="description"
          label="Description"
          placeholder="Short description shown to staff"
        />
        <FormikRichTextEditor<InsurancePolicyProductFormValues>
          name="insuranceRequirements"
          label="Insurance requirements"
        />
        <FormikSwitch<InsurancePolicyProductFormValues>
          name="requireDocumentUploadWhenOptedIn"
          label="Require file upload when opted in"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="documentUploadTitleWhenOptedIn"
          label="Document title when opted in"
          placeholder="e.g. Certificate of Insurance"
        />
        <Typography variant="h4" css={tw`text-shades-900`}>
          Manual requirements
        </Typography>
        {/* Four fixed inputs, not a repeatable list: the backend caps the array at four
            (@ArrayMaxSize(4)), so a fifth row would only produce a 400. Blanks are dropped. */}
        <FormikTextField<InsurancePolicyProductFormValues>
          name="manualUploadRequirement1"
          label="Requirement 1"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="manualUploadRequirement2"
          label="Requirement 2"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="manualUploadRequirement3"
          label="Requirement 3"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="manualUploadRequirement4"
          label="Requirement 4"
        />
      </div>

      <div css={tw`flex flex-col gap-4`}>
        <Typography variant="h4" css={tw`text-shades-900`}>
          Coverage
        </Typography>
        <FormikTextField<InsurancePolicyProductFormValues>
          name="coverage"
          label="Coverage"
          placeholder="e.g. $1,000,000"
        />
        <FormikTextField<InsurancePolicyProductFormValues>
          name="coverageDetailsPdfUrl"
          label="Coverage PDF URL"
          placeholder="https://…"
        />
        <FormikRichTextEditor<InsurancePolicyProductFormValues>
          name="coverageDetails"
          label="Coverage details"
        />
      </div>

      {/* Hidden, not disabled, when editing: approval type applies to NEWLY created steps
          only, and the update DTO does not accept it — showing a value the form cannot act
          on would misinform. */}
      {isEdit ? null : (
        <div css={tw`flex flex-col gap-4`}>
          <Typography variant="h4" css={tw`text-shades-900`}>
            Approval
          </Typography>
          <FormikSelect<InsurancePolicyProductFormValues>
            name="approvalType"
            label="Approval type for new steps"
            options={INSURANCE_APPROVAL_TYPE_OPTIONS}
          />
          <Typography variant="body2" css={tw`text-shades-500`}>
            Only set when creating — it can&apos;t be changed later.
          </Typography>
        </div>
      )}

      <div css={tw`flex flex-col gap-4`}>
        <Typography variant="h4" css={tw`text-shades-900`}>
          Advanced
        </Typography>
        <Fragment>
          <FormikSwitch<InsurancePolicyProductFormValues>
            name="lockCollectedDocuments"
            label="Lock previously collected documents"
          />
          <Typography variant="body2" css={tw`text-shades-500`}>
            When on, documents contractors already submitted stay locked as they are. When off,
            contractors may be asked to resubmit.
          </Typography>
        </Fragment>
      </div>
    </div>
  )
}

export const InsurancePolicyProductFormContent = memo(InsurancePolicyProductFormContentComponent)
